/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from "react";
import { useAppTheme } from "../../utils/theme";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, Card, CardContent, Chip } from "@mui/material";
import { useUserStore } from "../../stores/user.store";
import { useCurrencySetting } from "../../hooks/useSetting";
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import { toTitleCase } from "../../utils/toTitleCase";
import DataTable, { HeadCell, createDataRow } from "../../components/DataTable";
import ReportFilters, { ReportFiltersProps } from "./ReportFilters";
import { buttonExport, runReportButton } from "./Report.styles";
import { useQueryClient } from "@tanstack/react-query";
import { useSettings } from "../../hooks/useSetting";
import { formatCommaSeparatedNumber } from "../../utils/general";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useGetProvidersByProgramId } from "../../hooks/useBeneficiaries";
import { ValidQlikOptions } from "../../domain/report.interface";
import { useGetOrganizationList } from "../../hooks/useOrganizations";
import { useBeneficiaryTerm } from "../../hooks/useBeneficiaryTerm";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { QlikEmbed } = require("@qlik/embed-react");

const MultiTreatmentBeneficiaryReport: React.FC = () => {
	const theme = useAppTheme();
	const queryClient = useQueryClient();
	const { userInfo } = useUserStore();
	const currencySetting = useCurrencySetting();
	const createAuditlogMutation = useCreateAuditlog();
	const { data: settings } = useSettings(userInfo?.id);
	const { singular, plural } = useBeneficiaryTerm();

	const replaceTermInHeader = (header: string) =>
		header
			.replace(/\bBeneficiary\b/g, singular)
			.replace(/\bBeneficiaries\b/g, plural)
			.replace(/\bParticipant\b/g, singular)
			.replace(/\bParticipants\b/g, plural);

	const userProgramsIds =
		userInfo?.programs.map((program) => program.programId) || [];

	const [filters, setFilters] = useState<ReportFiltersProps>({
		programId: userProgramsIds.length > 0 ? [userProgramsIds[0]] : [],
		providerSiteId: [],
		providerId: "",
		providerNpi: "",
		beneficiary: "",
		beneficiaryCin: "",
	});

	//get the OUs
	const {
		data: providerSitesData,
		isFetching: ouFetching,
		isLoading: ouLoading,
	} = useGetOrganizationList(userInfo?.accountId || "");

	//get providers
	const {
		isLoading: isLoadingProviders,
		isFetching: isFetchingProviders,
		data: providers,
	} = useGetProvidersByProgramId(
		filters.programId,
		filters.providerSiteId,
		false,
	);

	// Qlik-related state
	const APP_ID = process.env.REACT_APP_QLIK_APP_ID || "";
	const TABLE_CONTAINER_ID =
		settings?.qlikBeneficiaryReportConfig?.tableObjectId;
	const FILTERS_OBJECT_ID =
		settings?.qlikBeneficiaryReportConfig?.filtersObjectId;
	const EXPORT_OBJECT_ID =
		settings?.qlikBeneficiaryReportConfig?.detailReport || "";

	const [qlikApi, setQlikApi] = useState<any>(null);
	const [containerModel, setContainerModel] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(false);

	// Main report (first child)
	const [tableData1, setTableData1] = useState<Array<
		Array<{ qText: string; qNum?: number }>
	> | null>(null);

	const [validQlikOptions, setValidQlikOptions] = useState<ValidQlikOptions>(
		{},
	);
	const [headers1, setHeaders1] = useState<string[]>([]);

	// Sub report (second child)
	const [tableData2, setTableData2] = useState<Array<
		Array<{ qText: string; qNum?: number }>
	> | null>(null);
	const [headers2, setHeaders2] = useState<string[]>([]);

	const [selectedKey, setSelectedKey] = useState<string | null>(null);

	const [, setSearchReport] = useState(false);

	const postAuditlog = async ({
		action,
		actionDetail,
		actionCode,
	}: {
		action: string;
		actionDetail: string;
		actionCode: string;
	}) => {
		await createAuditlogMutation.mutateAsync({
			appType: "WEB_BACK_OFFICE",
			module: "Reporting",
			option: `${singular} Report`,
			actionCode,
			action,
			detail: actionDetail,
			createdBy: userInfo?.id || "",
		});
	};

	useEffect(() => {
		if (!qlikApi || !TABLE_CONTAINER_ID) return;
		(async () => {
			const { app } = await qlikApi.getDoc();
			const obj = await app.getObject(TABLE_CONTAINER_ID);
			setContainerModel(obj);
		})();
	}, [qlikApi, TABLE_CONTAINER_ID]);

	// Qlik data fetching logic - container with child reports
	const fetchData = async () => {
		setIsLoading(true);
		try {
			if (!containerModel) return;
			await containerModel.app.clearAll();

			//TODO add acccountId env.accountId
			const programIds = filters.programId.flatMap((item) =>
				typeof item === "string" && item.includes(",")
					? item.split(",").map((id) => id.trim())
					: [item],
			);
			const qValues = Array.isArray(programIds)
				? programIds.map((v) => ({ qText: v }))
				: [{ qText: programIds }];

			await containerModel.app
				.getField("program_id")
				.then((f: any) => f.selectValues(qValues, false, false))
				.catch((error: any) => {
					console.log("Field program_id not found, skipping selection:", error);
					return Promise.resolve();
				});

			await fetchChildReports();
		} catch (error) {
			console.error("Error fetching Qlik data:", error);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!qlikApi || !containerModel) return;
		fetchData();
		fetchValidQlikOptions();
	}, [qlikApi, containerModel]);

	const fetchValidQlikOptions = async () => {
		if (!qlikApi || !TABLE_CONTAINER_ID) return;

		try {
			const containerLayout: any = await containerModel.getLayout();
			const items = containerLayout.qChildList.qItems || [];
			const childId = items[0].qInfo.qId;
			const childModel = await (await qlikApi.getDoc()).app.getObject(childId);
			const layout = await childModel.getLayout();

			if (!layout.qHyperCube?.qDimensionInfo) return;

			const { qDimensionInfo } = layout.qHyperCube;
			const { qcy: totalRows, qcx: totalCols } = layout.qHyperCube.qSize;
			const pageSize = 1000;
			const numPages = Math.ceil(totalRows / pageSize);

			// Fetch all data pages
			const pages = await Promise.all(
				Array.from({ length: numPages }, (_, i) => {
					const qTop = i * pageSize;
					const qHeight = Math.min(pageSize, totalRows - qTop);
					return childModel
						.getHyperCubeData({
							qPath: "/qHyperCubeDef",
							qPages: [{ qLeft: 0, qTop, qWidth: totalCols, qHeight }],
						})
						.then((p: any[]) => p[0]);
				}),
			);

			// Flatten all rows
			const allRows = pages.flatMap((p) => p.qMatrix);

			// For each dimension, collect unique values with valid qState
			const validOptions: ValidQlikOptions = {};
			qDimensionInfo.forEach((dim: any, dimIdx: number) => {
				const fieldName = dim.qFallbackTitle;
				const fieldExpression = dim.qEffectiveDimensionName;

				// Collect unique values with valid qState for this dimension
				const valuesSet = new Set<string>();
				allRows.forEach((row) => {
					const cell = row[dimIdx];
					if (
						cell &&
						(cell.qState === "O" ||
							cell.qState === "S" ||
							cell.qState === "A") &&
						cell.qText &&
						cell.qText.trim() !== ""
					) {
						valuesSet.add(cell.qText);
					}
				});
				const uniqueValidValues = Array.from(valuesSet);

				if (fieldExpression === "program_id") {
					validOptions.programIds = uniqueValidValues;
				} else if (fieldExpression === "organizations.name") {
					validOptions.orgUnitIds = uniqueValidValues;
				} else if (
					fieldExpression === "=[users.firstName]&' '&[users.lastName]"
				) {
					validOptions.providerIds = uniqueValidValues;
				} else if (
					fieldName?.toLowerCase().includes("npi") ||
					fieldExpression?.toLowerCase().includes("npi")
				) {
					validOptions.providerNpis = uniqueValidValues;
				}
			});

			setValidQlikOptions(validOptions);
		} catch (error) {
			console.error("Error fetching valid Qlik options from table:", error);
		}
	};

	async function fetchChildReports() {
		const layout: any = await containerModel.getLayout();
		const items = layout.qChildList.qItems || [];
		for (let i = 0; i < 2 && i < items.length; i++) {
			const childId = items[i].qInfo.qId;
			const childModel = await (await qlikApi.getDoc()).app.getObject(childId);

			childModel.on("changed", async () => {
				console.log(`childModel ${i} changed`);
				const dataSetter = i === 0 ? setTableData1 : setTableData2;
				const headerSetter = i === 0 ? setHeaders1 : setHeaders2;
				await fetchHypercubeData(childModel, dataSetter, headerSetter, i === 0);
				await fetchValidQlikOptions();
			});

			const dataSetter = i === 0 ? setTableData1 : setTableData2;
			const headerSetter = i === 0 ? setHeaders1 : setHeaders2;
			await fetchHypercubeData(childModel, dataSetter, headerSetter, i === 0);
			await fetchValidQlikOptions();
		}
	}

	// Apply filters to Qlik
	useEffect(() => {
		// Wait until we have valid filters and data is loaded before applying
		if (
			!qlikApi ||
			!filters.programId.length ||
			!filters.providerSiteId.length ||
			ouFetching ||
			ouLoading ||
			!providerSitesData
		)
			return;
		setQlikFilters();
	}, [filters, qlikApi, ouFetching, ouLoading, providerSitesData, selectedKey]);

	const setQlikFilters = async () => {
		if (!qlikApi || !containerModel?.app) return;

		try {
			await containerModel.app.clearAll();

			// Use variables for container-child pattern like in Dashboard
			const promises = [];

			// Try direct field selections for other fields that might not have variables
			const select = (fieldName: string, values: string | string[]) => {
				const qValues = Array.isArray(values)
					? values.map((v) => ({ qText: v }))
					: [{ qText: values }];
				return containerModel.app
					.getField(fieldName)
					.then((f: any) => f.selectValues(qValues, false, false))
					.catch((error: any) => {
						console.log(
							`Field ${fieldName} not found, skipping selection:`,
							error,
						);
						return Promise.resolve();
					});
			};

			if (filters.providerSiteId && filters.providerSiteId.length > 0) {
				if (!ouLoading && !ouFetching && providerSitesData) {
					const ouNameList = providerSitesData
						.filter((ou) => filters.providerSiteId.includes(ou.id))
						.map((oun) => oun.id);
					promises.push(
						containerModel.app
							.getVariableByName("vSelectedOU")
							.then((v: any) => v.setStringValue(ouNameList.join("|"))),
					);
				}
			}

			if (filters.providerId) {
				if (!isLoadingProviders && !isFetchingProviders && providers) {
					const providerNameList = providers
						.filter((p) => p.key === filters.providerId)
						.map((p) => p.key);
					promises.push(
						containerModel.app
							.getVariableByName("vSelectedProvider")
							.then((v: any) => v.setStringValue(providerNameList.join("|"))),
					);
				}
			}

			if (filters.providerNpi) {
				promises.push(select("NPI Provider", filters.providerNpi));
			}
			if (filters.beneficiary) {
				promises.push(
					select(
						"=[beneficiarie_firstName]&' '&[beneficiaries_lastName]",
						filters.beneficiary,
					),
				);
			}
			if (filters.beneficiaryCin) {
				promises.push(select("sims_clientIdNumber", filters.beneficiaryCin));
			}

			await Promise.all(promises);

			if (filters.programId && filters.programId.length > 0) {
				const programIds = filters.programId.flatMap((item) =>
					typeof item === "string" && item.includes(",")
						? item.split(",").map((id) => id.trim())
						: [item],
				);
				await containerModel.app
					.getVariableByName("vSelectedProgram")
					.then((v: any) => v.setStringValue(programIds.join("|")));
			}

			// Re-apply enrollment ID filter if we're viewing beneficiary details
			await applyEnrollmentIdFilter(selectedKey || "");

			setSearchReport(false);
		} catch (error) {
			console.error("Error setting Qlik filters:", error);
		}
	};

	async function fetchHypercubeData(
		model: any,
		dataSetter: React.Dispatch<
			React.SetStateAction<Array<
				Array<{ qText: string; qNum?: number }>
			> | null>
		>,
		headerSetter: React.Dispatch<React.SetStateAction<string[]>>,
		isMainTable = false,
	) {
		try {
			const layout = await model.getLayout();
			const dimHeaders = layout.qHyperCube.qDimensionInfo.map(
				(d: any) => d.qFallbackTitle,
			);
			const measHeaders = layout.qHyperCube.qMeasureInfo.map(
				(m: any) => m.qFallbackTitle,
			);
			const allHeaders = [...dimHeaders, ...measHeaders].filter(
				(h: string) => h !== "ProgramID" && h !== "",
			);
			headerSetter(allHeaders);

			const { qcy: totalRows, qcx: totalCols } = layout.qHyperCube.qSize;

			const pageSize = 1000;
			const numPages = Math.ceil(totalRows / pageSize);

			const pages = await Promise.all(
				Array.from({ length: numPages }, (_, i) => {
					const qTop = i * pageSize;
					const qHeight = Math.min(pageSize, totalRows - qTop);
					return model
						.getHyperCubeData({
							qPath: "/qHyperCubeDef",
							qPages: [{ qLeft: 0, qTop, qWidth: totalCols, qHeight }],
						})
						.then((p: any[]) => p[0]);
				}),
			);

			const rows = pages
				.flatMap((p) => p.qMatrix)
				.filter((row) =>
					row.some(
						(cell: any) =>
							cell.qText &&
							String(cell.qText).trim() !== "" &&
							String(cell.qText).toLowerCase() !== "nan",
					),
				);
			// Remove last 2 columns only for main table
			const filtered = isMainTable
				? rows.map((row: any[]) => row.slice(0, -2))
				: rows;

			dataSetter(filtered);
			setIsLoading(false);
		} catch (error) {
			console.error("Error fetching hypercube data:", error);
			setIsLoading(false);
		}
	}

	const pullExportData = async (
		qlikApi: any,
		exportObjectId: string,
	): Promise<{ headers: string[]; rows: string[][] }> => {
		const { app } = await qlikApi.getDoc();
		const model = await app.getObject(exportObjectId);

		const layout: any = await model.getLayout();

		// Get dimension and measure info
		const dims = layout.qHyperCube.qDimensionInfo.map(
			(d: any) => d.qFallbackTitle,
		);
		const measures = layout.qHyperCube.qMeasureInfo.map(
			(m: any) => m.qFallbackTitle,
		);

		// Use qColumnOrder which shows the actual data column positions
		const sortOrder = layout.qHyperCube.qColumnOrder;

		let headers: string[];

		if (sortOrder && sortOrder.length > 0) {
			// Use the column order to arrange headers correctly
			const allHeaders = [...dims, ...measures];
			headers = sortOrder.map((colIndex: number) => allHeaders[colIndex]);
		} else {
			// Fallback to default order: dimensions first, then measures
			headers = [...dims, ...measures];
		}

		const { qcy: totalRows, qcx: totalCols } = layout.qHyperCube.qSize;
		const pageSize = 200;
		const numPages = Math.ceil(totalRows / pageSize);
		const pages = await Promise.all(
			Array.from({ length: numPages }, (_, i) => {
				const qTop = i * pageSize;
				const qHeight = Math.min(pageSize, totalRows - qTop);
				return model
					.getHyperCubeData({
						qPath: "/qHyperCubeDef",
						qPages: [{ qLeft: 0, qTop, qWidth: totalCols, qHeight }],
					})
					.then(
						(p: any[]) =>
							p[0].qMatrix as Array<Array<{ qText: string; qNum?: number }>>,
					);
			}),
		);
		const matrix = pages.flat();

		const rows = matrix
			.map((row) => row.map((cell: any) => String(cell.qText)))
			.sort((a, b) => {
				const cmp = a[0].localeCompare(b[0]);
				if (cmp !== 0) return cmp;
				const dateA = new Date(a[14]);
				const dateB = new Date(b[14]);
				return dateA.getTime() - dateB.getTime();
			})
			.map((r) =>
				r.filter(
					(_: any, idx: any) => idx !== layout.qHyperCube.qDimensionInfo.length,
				),
			);

		headers = headers.filter((h) => h !== "ProgramID").map(replaceTermInHeader);

		return { headers, rows };
	};

	const handleExportExcel = async () => {
		if (!qlikApi || !EXPORT_OBJECT_ID) return;

		const { headers, rows } = await pullExportData(qlikApi, EXPORT_OBJECT_ID);

		// Remove last column and filter out rows with all meaningless values
		const processedRows = rows
			.map((r) => r.slice(0, -1)) // Remove last column
			.filter((row) => {
				// Check if row has at least one meaningful value
				return row.some(
					(cell) =>
						cell !== undefined &&
						cell !== null &&
						String(cell).trim() !== "" &&
						String(cell).trim() !== "-" &&
						String(cell).toLowerCase() !== "nan" &&
						String(cell).trim() !== "0" &&
						String(cell).trim() !== "$0.00",
				);
			});

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet([headers, ...processedRows]);
		ws["!cols"] = headers.map(() => ({ wch: 20 }));
		XLSX.utils.book_append_sheet(wb, ws, `${singular} Report`);
		XLSX.writeFile(wb, `${singular.toLowerCase()}_report.xlsx`);
	};

	const handleExportPdf = async () => {
		if (!qlikApi || !EXPORT_OBJECT_ID) return;

		const { headers, rows } = await pullExportData(qlikApi, EXPORT_OBJECT_ID);

		// Remove last column and filter out rows with all meaningless values
		const processedRows = rows
			.map((r) => r.slice(0, -1)) // Remove last column
			.filter((row) => {
				// Check if row has at least one meaningful value
				return row.some(
					(cell) =>
						cell !== undefined &&
						cell !== null &&
						String(cell).trim() !== "" &&
						String(cell).trim() !== "-" &&
						String(cell).toLowerCase() !== "nan" &&
						String(cell).trim() !== "0" &&
						String(cell).trim() !== "$0.00",
				);
			});

		const doc = new jsPDF({
			unit: "pt",
			format: "A4",
			orientation: "landscape",
		});

		autoTable(doc, {
			head: [headers],
			body: processedRows,
			styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
			headStyles: { fillColor: [41, 128, 185] },
			theme: "striped",
			margin: { top: 40, left: 40, right: 40, bottom: 40 },
		});

		doc.save(`${singular.toLowerCase()}_report.pdf`);
	};

	const onFiltersChange = (newFilters: ReportFiltersProps) => {
		setFilters(newFilters);
	};

	// Helper function to apply enrollment ID filter
	const applyEnrollmentIdFilter = async (enrollmentId: string) => {
		if (!containerModel?.app) return false;

		try {
			const field = await containerModel.app.getField("Id_enrollmentsUDT");
			if (field) {
				await field.selectValues([{ qText: enrollmentId }], false, false);
				return true;
			}
			return false;
		} catch (error) {
			console.error("Error applying enrollment ID filter:", error);
			return false;
		}
	};

	// Handle cell click for subreport - filter the child object directly
	const handleCellClick = async (key: string) => {
		setSelectedKey(key);
		if (!qlikApi) return;
		try {
			const { app } = await qlikApi.getDoc();
			await app.clearAll();

			try {
				// Try different possible field names for the beneficiary/enrollment identifier
				const field = await app.getField("Id_enrollmentsUDT");

				if (field) {
					await field.selectValues([{ qText: key }], false, false);
				}
			} catch (fieldError) {
				console.error("Field selection failed:", fieldError);
			}
		} catch (error) {
			console.error("Error handling cell click:", error);
		}
	};

	// Main report rows (tableData1)
	const rows1 = useMemo(() => {
		if (!tableData1 || tableData1.length === 0) return [];

		return tableData1.map((rowArray, index) =>
			createDataRow(
				index,
				rowArray.reduce(
					(rec, cell, cidx) => {
						// Hide the first column (CIN) - we'll use its value for clicking the second column
						if (cidx === 0) {
							// Store the CIN value but don't render it visibly
							rec[`t1_col${cidx}`] = cell.qText; // Just store the plain text, no UI
						} else if (cidx === 1) {
							// Make the second column (Beneficiary) clickable using the first column's value
							const cinValue = rowArray[0].qText; // Get CIN from first column
							rec[`t1_col${cidx}`] = (
								<span
									style={{
										cursor: "pointer",
										color: "#1976d2",
										textDecoration: "underline",
									}}
									onClick={() => {
										setIsLoading(true);
										handleCellClick(cinValue); // Use CIN value for filtering
									}}
								>
									{cell.qText}
								</span>
							);
						} else if (cidx === 9) {
							// Status column
							rec[`t1_col${cidx}`] = (
								<Chip
									sx={{
										bgcolor:
											cell.qText === "ACTIVE"
												? theme.palette.secondary.light
												: theme.palette.outline.dark,
									}}
									label={
										<div className="text-xs">{toTitleCase(cell.qText)}</div>
									}
								/>
							);
						} else if (cidx >= 9 && cidx <= 11) {
							// Currency columns (incentive earned, received, bank balance)
							rec[`t1_col${cidx}`] = (
								<span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
									{`${currencySetting.mask}${formatCommaSeparatedNumber(cell.qNum || 0)}`}
								</span>
							);
						} else {
							rec[`t1_col${cidx}`] = (
								<span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
									{cell.qText}
								</span>
							);
						}
						return rec;
					},
					{} as Record<string, React.ReactNode>,
				),
			),
		);
	}, [tableData1, currencySetting, theme]);

	// Sub report rows (tableData2)
	const rows2 = useMemo(() => {
		if (!tableData2 || tableData2.length === 0) return [];
		return (
			tableData2
				//.filter((r) => r[2]?.qText !== "-") // Filter out empty rows
				.map((rowArray, index) =>
					createDataRow(
						index,
						rowArray.reduce(
							(rec, cell, cidx) => {
								rec[`t2_col${cidx}`] = (
									<span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
										{cell.qText}
									</span>
								);
								return rec;
							},
							{} as Record<string, React.ReactNode>,
						),
					),
				)
		);
	}, [tableData2]);

	// Dynamic head cells and rows based on selected state
	const { headCells, adjustedRows } = useMemo(() => {
		if (selectedKey) {
			// Show subreport (tableData2) with dynamic headers
			const hc: HeadCell[] = headers2.map((lbl, idx) => ({
				id: `t2_col${idx}`,
				label: replaceTermInHeader(lbl),
				isSortable: false,
				disablePadding: false,
				align: "left" as const,
				width: "150px",
			}));
			return { headCells: hc, adjustedRows: rows2 };
		} else {
			// Show main report (tableData1) with dynamic headers from first child
			if (!headers1 || headers1.length === 0) {
				return { headCells: [], adjustedRows: [] };
			}

			// Store the original last column index before any modifications
			const originalLastColIndex = headers1.length;

			let hc: HeadCell[] = headers1.map((lbl, idx) => ({
				id: `t1_col${idx}`,
				label: replaceTermInHeader(lbl),
				isSortable: false,
				disablePadding: false,
				align: "left" as const,
			}));

			let adjRows = rows1;

			// Always hide the first column (CIN column at index 0)
			hc = hc.filter((_, idx) => idx !== 0);
			adjRows = rows1.map((row) => {
				const newRow = { ...row };
				delete newRow.t1_col0; // Remove first column (CIN)
				return newRow;
			});

			// Handle displayBeneficiaryName setting by removing beneficiary name column (now at index 1 after removing col0)
			if (settings && !settings.displayBeneficiaryName) {
				hc = hc.filter((cell) => cell.id !== "t1_col2");
				adjRows = adjRows.map((row) => {
					const newRow = { ...row };
					delete newRow.t1_col2; // Remove beneficiary name column
					return newRow;
				});
			}

			// Remove the last column from the main table (rows1 only)
			// Use the original index since row data keys are based on original headers
			hc = hc.filter((cell) => cell.id !== `t1_col${originalLastColIndex}`);
			adjRows = adjRows.map((row) => {
				const newRow = { ...row };
				delete newRow[`t1_col${originalLastColIndex}`]; // Remove last column
				return newRow;
			});

			adjRows = adjRows.filter((row) => {
				const cellValues = Object.entries(row)
					.filter(([key]) => key !== "id" && key !== "style")
					.map(([, value]) => {
						if (React.isValidElement(value)) {
							return value.props.children;
						}
						return value;
					});

				return cellValues.some(
					(val) =>
						val !== undefined &&
						val !== null &&
						String(val).trim() !== "" &&
						String(val).trim() !== "-" &&
						String(val).toLowerCase() !== "nan" &&
						String(val).trim() !== "$0.00",
				);
			});

			return { headCells: hc, adjustedRows: adjRows };
		}
	}, [selectedKey, headers1, headers2, rows1, rows2, settings]);

	// Audit log for initial access and fetch data on mount
	useEffect(() => {
		postAuditlog({
			action: "When entering",
			actionDetail: `Access to multi-treatment ${singular.toLowerCase()} report`,
			actionCode: "WEB_REPORT_BENEF_ACCESS",
		});
	}, []);

	useEffect(() => {
		return () => {
			queryClient.removeQueries({
				queryKey: ["sims-report-beneficiary"],
			});
		};
	}, [queryClient]);

	return (
		<>
			<div className="w-full h-full overflow-y-auto scroll-smooth">
				<div className="container">
					<div className="flex flex-col items-center justify-start min-h-screen space-y-4">
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "flex-start",
								width: "100%",
							}}
						>
							<Typography variant="headlineSmall">
								{selectedKey ? `${singular} Details` : `${singular} report`}{" "}
								<InfoOutlinedIcon />
							</Typography>
						</Box>

						<Card sx={{ minWidth: 275, width: "100%" }}>
							<CardContent className="space-y-5">
								{selectedKey ? null : (
									<Box
										sx={{
											display: "flex",
											gap: "1rem",
										}}
									>
										<Button
											size="large"
											variant="outlined"
											sx={buttonExport(theme)}
											onClick={handleExportExcel}
											disabled={
												isLoading || !tableData1 || tableData1.length === 0
											}
										>
											DOWNLOAD EXCEL
										</Button>
										<Button
											size="large"
											variant="outlined"
											sx={buttonExport(theme)}
											onClick={handleExportPdf}
											disabled={
												isLoading || !tableData1 || tableData1.length === 0
											}
										>
											DOWNLOAD PDF
										</Button>
									</Box>
								)}

								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: {
											xs: "repeat(1, 1fr)",
											sm: "repeat(2, 1fr)",
											md: "repeat(4, 1fr)",
											lg: "repeat(5, 1fr)",
										},
										gap: "1rem",
										width: "100%",
									}}
								>
									{selectedKey || !containerModel ? null : (
										<ReportFilters
											onFiltersChange={onFiltersChange}
											filters={filters}
											showBeneficiaryFilters={settings?.displayBeneficiaryName}
											validOptions={validQlikOptions}
										/>
									)}
									<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
										{!selectedKey ? (
											<></>
										) : (
											<Button
												size="large"
												variant="outlined"
												onClick={async () => {
													setIsLoading(true);
													setSelectedKey(null);
													await setQlikFilters();
													setIsLoading(false);
												}}
												sx={runReportButton(theme)}
											>
												GO BACK
											</Button>
										)}

										{/* Hidden QlikEmbed component for API access */}
										<Box
											sx={{
												opacity: 0,
												visibility: "hidden",
												width: 0,
												height: 0,
											}}
										>
											<QlikEmbed
												ui="analytics/chart"
												appId={APP_ID}
												objectId={FILTERS_OBJECT_ID}
												identity={`${userInfo?.id}-beneficiary`}
												style={{ width: "100%", height: "100%" }}
												exposeRefApi={(ref: any) => {
													if (!qlikApi && ref) {
														setQlikApi(ref);
													}
												}}
											/>
										</Box>
									</Box>
								</Box>

								<DataTable
									key={selectedKey || "mainTable"}
									data={adjustedRows}
									rowsPerPage={10}
									order="asc"
									orderBy="none"
									page={0}
									headCells={headCells}
									total={adjustedRows.length}
									isLoading={isLoading || !containerModel}
									useFrontendPagination={true}
								/>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</>
	);
};

export default MultiTreatmentBeneficiaryReport;
